import * as helios from "@hyperionbt/helios";
import * as Sentry from "@sentry/nextjs";
import { excludeNullishElements } from "common-utils/utils/array";
import { Artwork } from "kreate-common/modules/business-types";
import { httpPost$MintNativeScriptToken } from "kreate-server/commands/MintNativeScriptToken/fetcher";
import { MdFavorite } from "react-icons/md";

import { getAddressFromCip30Wallet } from "@/modules/cardano-wallet/utils";
import { waitForUtxo } from "@/modules/common-utils/async";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import {
  getAssetFingerprint,
  getPoolPmUrl,
} from "@/modules/kreate-components/components/LinkPoolPm/utils";
import { waitModal } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import ModalProgressV2 from "@/modules/kreate-modals/modals/ModalProgressV2";
import { anticipateSteps } from "@/modules/kreate-modals/modals/ModalProgressV2/utils";
import ModalSuccess from "@/modules/kreate-modals/modals/ModalSuccess";

const steps = anticipateSteps({
  buildingTransaction: 3500,
  waitingForSignatureAndSubmission: 10500,
  waitingForConfirmation: 24000,
});

type Params = {
  artwork: Artwork;
  cip30Wallet: helios.Cip30Wallet;
  numMinting: number;
};

export async function* runFlow$MintArtworkMintingRulesV1({
  artwork,
  cip30Wallet,
  numMinting,
}: Params) {
  const timestamps: number[] = [];

  console.time("runFlow$MintArtworkMintingRulesV1");
  timestamps.push(Date.now());
  yield (
    <ModalProgressV2
      open
      content="Building transaction..."
      ratio={steps.buildingTransaction.ratio}
      expectedDuration={steps.buildingTransaction.duration}
    />
  );

  const address = await getAddressFromCip30Wallet(cip30Wallet);
  const utxos = await cip30Wallet.utxos;

  const result = await httpPost$MintNativeScriptToken(
    "/api/v1/tx/mint-native",
    {
      artworkId: artwork.artworkId,
      address,
      utxosCBOR: utxos.map((utxo) => helios.bytesToHex(utxo.toCbor())),
    }
  );

  const tx = helios.Tx.fromCbor(result.tx);
  tx.body.setScriptDataHash(null);

  console.timeLog("runFlow$MintArtworkMintingRulesV1");
  timestamps.push(Date.now());
  yield (
    <ModalProgressV2
      open
      content="Waiting for signature and submission..."
      ratio={steps.waitingForSignatureAndSubmission.ratio}
      expectedDuration={steps.waitingForSignatureAndSubmission.duration}
    />
  );

  const signatures = await cip30Wallet.signTx(tx);
  tx.addSignatures(signatures);
  const txId = await cip30Wallet
    .submitTx(tx)
    .then((heliosTxId) => heliosTxId.hex);

  console.timeLog("runFlow$MintArtworkMintingRulesV1");
  timestamps.push(Date.now());
  yield (
    <ModalProgressV2
      open
      content="Waiting for confirmation..."
      ratio={steps.waitingForConfirmation.ratio}
      expectedDuration={steps.waitingForConfirmation.duration}
    />
  );

  await waitForUtxo(cip30Wallet, (utxo) => utxo.txId.hex === txId);
  await shamelesslyRevalidateEverything();
  const assetFingerprint = getAssetFingerprint(artwork);
  const poolPmUrl = assetFingerprint
    ? getPoolPmUrl(assetFingerprint)
    : undefined;

  console.timeEnd("runFlow$MintArtworkMintingRulesV1");
  timestamps.push(Date.now());
  yield* waitModal<void>((resolve) => (
    <ModalSuccess
      open
      icon={<MdFavorite />}
      content={`You have minted ${numMinting} Editions.`}
      onClose={resolve}
      buttons={excludeNullishElements([
        poolPmUrl
          ? {
              content: "View on Pool.pm",
              onClick: { type: "open", href: poolPmUrl, newTab: true },
              isPrimary: true,
            }
          : undefined,
        { content: "Close", onClick: "close" },
      ])}
    />
  ));

  // NOTE: @kien-kreate: report the logs by console.time, console.timeLog, console.timeEnd
  Sentry.captureEvent({
    message: "runFlow$UpdateMintingDetails",
    level: "info",
    extra: { timestamps },
  });
}
